import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, FileText, UserPlus, ArrowRight, Calendar, Users, Loader2 } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { BASE_URL } from '@/config/api';

const fetchInvoice = async (id: string, token: string | null) => {
  const res = await fetch(`${BASE_URL}/api/invoice/${id}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      Accept: 'application/json',
    },
  });
  if (!res.ok) throw new Error('Failed');
  const data = await res.json();
  return data.invoice;
};

const formatMoney = (v: any, currency: string | undefined, locale: string) => {
  const n = Number(v) || 0;
  try {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', { style: 'currency', currency: currency || 'EUR' }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency || '€'}`;
  }
};

const InvoiceCard = ({ id, index }: { id: string; index: number }) => {
  const { token } = useAuth();
  const { t, locale } = useLanguage();
  const navigate = useNavigate();
  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: ['invoice-split', id],
    queryFn: () => fetchInvoice(id, token),
  });

  const recipient =
    invoice?.name || invoice?.client_name || invoice?.parent?.name || invoice?.parent_name || invoice?.student?.name || '—';
  const studentName = invoice?.student_name || invoice?.student?.name;

  return (
    <div className="group relative flex flex-col rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:shadow-md hover:border-primary/40">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <FileText size={20} />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              {t('students.splitCreatedInvoice', { index: index + 1 })}
            </p>
            <p className="text-xs text-muted-foreground">
              {invoice?.number ? `N° ${invoice.number}` : `#${id}`}
            </p>
          </div>
        </div>
        {invoice?.status && (
          <Badge variant="secondary" className="capitalize">
            {invoice.status === 'pending' ? t('students.splitCreatedPending') : invoice.status}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : isError ? (
        <p className="flex-1 py-6 text-sm text-muted-foreground">{t('students.splitCreatedLoadError')}</p>
      ) : (
        <div className="flex-1 space-y-3 text-sm">
          <div className="flex items-start gap-2">
            <Users size={16} className="mt-0.5 text-muted-foreground" />
            <div>
              <p className="text-muted-foreground text-xs">{t('students.splitCreatedRecipient')}</p>
              <p className="font-medium">{recipient}</p>
              {studentName && studentName !== recipient && (
                <p className="text-xs text-muted-foreground">{t('students.splitCreatedStudent')} : {studentName}</p>
              )}
            </div>
          </div>
          {(invoice?.period_starts || invoice?.date) && (
            <div className="flex items-start gap-2">
              <Calendar size={16} className="mt-0.5 text-muted-foreground" />
              <div>
                <p className="text-muted-foreground text-xs">
                  {invoice?.period_starts ? t('students.splitCreatedPeriod') : t('students.splitCreatedDate')}
                </p>
                <p className="font-medium">
                  {invoice?.period_starts
                    ? `${invoice.period_starts} → ${invoice.period_ends || ''}`
                    : invoice?.date}
                </p>
              </div>
            </div>
          )}
          <div className="mt-4 flex items-end justify-between rounded-xl bg-muted/50 p-4">
            <span className="text-muted-foreground">{t('students.splitCreatedTotal')}</span>
            <span className="text-2xl font-bold">{formatMoney(invoice?.total, invoice?.invoice_currency, locale)}</span>
          </div>
        </div>
      )}

      <Button className="mt-5 w-full" onClick={() => navigate(`/invoices/${id}`)}>
        {t('students.splitCreatedManage')} <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
};

const InvoiceSplitCreated = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const ids = (params.get('ids') || '').split(',').filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1 container mx-auto max-w-4xl px-4 pt-28 pb-16">
        <div className="mb-10 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 size={36} />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">{t('students.splitCreatedTitle', { count: ids.length })}</h1>
          <p className="mt-2 text-muted-foreground">{t('students.splitCreatedSubtitle')}</p>
        </div>

        <button
          onClick={() => navigate('/invoices/billing')}
          className="mb-10 flex w-full items-center gap-4 rounded-2xl border-2 border-dashed border-primary/40 bg-primary/5 p-5 text-left transition-colors hover:bg-primary/10"
        >
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground">
            <UserPlus size={22} />
          </div>
          <div className="flex-1">
            <p className="font-semibold">{t('students.splitCreatedBillAnother')}</p>
            <p className="text-sm text-muted-foreground">{t('students.splitCreatedBillAnotherHelp')}</p>
          </div>
          <ArrowRight className="text-primary" />
        </button>

        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('students.splitCreatedInvoicesTitle')}
        </h2>
        <div className="grid gap-5 md:grid-cols-2">
          {ids.map((id, i) => (
            <InvoiceCard key={id} id={id} index={i} />
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default InvoiceSplitCreated;
